// src/components/SalesDashboard/QuotationCreate.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Search, Plus, Trash2, ChevronLeft, ChevronRight,
  AlertTriangle, CheckCircle, Loader, ChevronDown, ChevronUp,
  User, Package, FileText, ClipboardList,
} from 'lucide-react';
import api from '../api';
import { useMasterData } from '../../utils/useMasterData';

// ── Styles ────────────────────────────────────────────────────────────────────
const S = `
  .qc-wrap { max-width: 920px; margin: 0 auto; font-family: 'Inter', sans-serif; color: #000; }
  .qc-stepper { display: flex; align-items: center; margin-bottom: 32px; }
  .qc-step { display: flex; align-items: center; flex: 1; }
  .qc-step-circle {
    width: 36px; height: 36px; border-radius: 50%; display: flex; align-items: center;
    justify-content: center; font-size: 14px; font-weight: 700; flex-shrink: 0;
    transition: all .2s;
  }
  .qc-step-circle.done { background: #6366f1; color: white; }
  .qc-step-circle.active { background: #1e1b4b; color: white; box-shadow: 0 0 0 4px #c7d2fe; }
  .qc-step-circle.pending { background: #e2e8f0; color: #94a3b8; }
  .qc-step-label { margin-left: 10px; font-size: 13px; font-weight: 600; color: #1a1a1a; }
  .qc-step-label.active { color: #1e1b4b; }
  .qc-connector { flex: 1; height: 2px; background: #e2e8f0; margin: 0 8px; }
  .qc-connector.done { background: #6366f1; }

  .qc-card { background: white; border-radius: 16px; border: 1px solid #e2e8f0; padding: 28px; margin-bottom: 20px; }
  .qc-section-title { font-size: 16px; font-weight: 700; color: #000; margin: 0 0 20px; }

  .qc-field { margin-bottom: 16px; }
  .qc-label { display: block; font-size: 12px; font-weight: 600; color: #1a1a1a; margin-bottom: 6px; text-transform: uppercase; letter-spacing: .5px; }
  .qc-input {
    width: 100%; padding: 10px 14px; border: 1.5px solid #e2e8f0; border-radius: 10px;
    font-size: 14px; color: #1e293b; outline: none; box-sizing: border-box; transition: border-color .15s;
  }
  .qc-input:focus { border-color: #6366f1; }
  .qc-input[readonly], .qc-input:disabled { background: #f8fafc; color: #64748b; }

  .qc-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
  .qc-grid-3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 16px; }
  .qc-grid-4 { display: grid; grid-template-columns: 1fr 1fr 1fr 1fr; gap: 12px; }

  .qc-autocomplete { position: relative; }
  .qc-dropdown {
    position: absolute; top: 100%; left: 0; right: 0; z-index: 100;
    background: white; border: 1.5px solid #e2e8f0; border-top: none;
    border-radius: 0 0 10px 10px; max-height: 240px; overflow-y: auto;
    box-shadow: 0 8px 24px rgba(0,0,0,.1);
  }
  .qc-dropdown-item {
    padding: 10px 14px; cursor: pointer; font-size: 13px; color: #1e293b;
    border-bottom: 1px solid #f1f5f9; transition: background .1s;
  }
  .qc-dropdown-item:hover { background: #f8fafc; }
  .qc-dropdown-item:last-child { border-bottom: none; }

  .qc-blacklist-banner {
    background: #fef2f2; border: 1.5px solid #fca5a5; border-radius: 10px;
    padding: 14px 18px; display: flex; align-items: flex-start; gap: 12px;
    margin-top: 12px; color: #dc2626;
  }

  .qc-customer-card {
    background: linear-gradient(135deg,#f8fafc,#f1f5f9); border: 1.5px solid #e2e8f0;
    border-radius: 12px; padding: 16px 20px; display: flex; align-items: center; gap: 16px;
    margin-top: 12px;
  }
  .qc-customer-avatar {
    width: 48px; height: 48px; border-radius: 50%; background: linear-gradient(135deg,#6366f1,#8b5cf6);
    display: flex; align-items: center; justify-content: center; color: white; font-size: 18px; font-weight: 700;
    flex-shrink: 0;
  }

  .qc-design-row {
    background: #f8fafc; border: 1.5px solid #e2e8f0; border-radius: 12px;
    padding: 20px; margin-bottom: 16px; position: relative;
  }
  .qc-bom-expand { margin-top: 14px; border-top: 1px solid #e2e8f0; padding-top: 14px; }
  .qc-bom-toggle {
    display: flex; align-items: center; gap: 6px; cursor: pointer; font-size: 12px;
    font-weight: 600; color: #6366f1; background: none; border: none; padding: 0;
  }
  .qc-bom-table { width: 100%; border-collapse: collapse; font-size: 12px; margin-top: 10px; }
  .qc-bom-table th { background: #f1f5f9; padding: 6px 10px; text-align: left; color: #1a1a1a; font-weight: 600; }
  .qc-bom-table td { padding: 6px 10px; border-bottom: 1px solid #f1f5f9; color: #000; }

  .qc-add-row-btn {
    display: flex; align-items: center; gap: 8px; padding: 10px 20px;
    background: #f1f5f9; border: 1.5px dashed #c7d2fe; border-radius: 10px;
    color: #6366f1; font-size: 14px; font-weight: 600; cursor: pointer;
    transition: all .2s; width: 100%;
  }
  .qc-add-row-btn:hover { background: #eef2ff; border-color: #6366f1; }

  .qc-remove-btn {
    position: absolute; top: 12px; right: 12px; background: #fef2f2; border: none;
    color: #ef4444; border-radius: 8px; padding: 6px 10px; cursor: pointer; font-size: 12px;
    display: flex; align-items: center; gap: 4px; font-weight: 600;
  }
  .qc-remove-btn:hover { background: #fee2e2; }

  .qc-totals-box {
    background: linear-gradient(135deg,#1e1b4b,#312e81); border-radius: 14px;
    padding: 24px; color: white; margin-top: 20px;
  }
  .qc-totals-row { display: flex; justify-content: space-between; align-items: center; padding: 6px 0; font-size: 14px; }
  .qc-totals-divider { border: none; border-top: 1px solid rgba(255,255,255,.2); margin: 8px 0; }
  .qc-totals-grand { font-size: 20px; font-weight: 800; }

  .qc-nav-row { display: flex; justify-content: space-between; align-items: center; margin-top: 24px; }
  .qc-btn-primary {
    padding: 12px 28px; background: linear-gradient(135deg,#6366f1,#4f46e5); color: white;
    border: none; border-radius: 10px; font-size: 14px; font-weight: 700; cursor: pointer;
    display: flex; align-items: center; gap: 8px; transition: opacity .2s;
  }
  .qc-btn-primary:hover { opacity: .9; }
  .qc-btn-primary:disabled { opacity: .5; cursor: not-allowed; }
  .qc-btn-secondary {
    padding: 12px 24px; background: white; color: #475569; border: 1.5px solid #e2e8f0;
    border-radius: 10px; font-size: 14px; font-weight: 600; cursor: pointer;
    display: flex; align-items: center; gap: 8px;
  }
  .qc-btn-secondary:hover { background: #f8fafc; }
  .qc-btn-success {
    padding: 12px 28px; background: linear-gradient(135deg,#10b981,#059669); color: white;
    border: none; border-radius: 10px; font-size: 14px; font-weight: 700; cursor: pointer;
    display: flex; align-items: center; gap: 8px;
  }
  .qc-btn-success:hover { opacity: .9; }
  .qc-btn-success:disabled { opacity: .5; cursor: not-allowed; }

  .qc-error-msg { color: #dc2626; font-size: 13px; margin-top: 6px; }
  .qc-success-block { text-align: center; padding: 48px 24px; }
`;

const STEPS = [
  { id: 1, label: 'Customer', icon: User },
  { id: 2, label: 'Design Items', icon: Package },
  { id: 3, label: 'Terms', icon: FileText },
  { id: 4, label: 'Summary', icon: ClipboardList },
];

const emptyItem = () => ({
  _key: Math.random(),
  design_id: null,
  design: null,
  quantity: 1,
  metal_type: '22K Gold',
  metal_rate: 0,
  making_charges: 0,
  stone_charges: 0,
  other_charges: 0,
  special_requirements: '',
  bom_open: false,
  design_search: '',
  design_results: [],
  design_loading: false,
});

const fmt = (n) => Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const buildQuotationSubject = (customerName) => {
  const name = (customerName || '').trim();
  return name ? `${name} Quotation` : '';
};

function computeItem(item) {
  const weight = parseFloat(item.design?.actual_weight || 0);
  const quantity = parseInt(item.quantity || 1, 10);
  const unit_metal_price = parseFloat(item.metal_rate || 0) * weight;
  const unit_making_price = parseFloat(item.making_charges || 0) * weight;
  const unit_stone_price = parseFloat(item.stone_charges || 0) * weight;
  const unit_other_price = parseFloat(item.other_charges || 0) * weight;
  const unit_price = unit_metal_price + unit_making_price + unit_stone_price + unit_other_price;
  const total_price = unit_price * quantity;
  return { unit_metal_price, unit_making_price, unit_stone_price, unit_other_price, unit_price, total_price };
}

function computeTotals(items, discountPct, taxPct) {
  const subtotal = items.reduce((s, it) => s + computeItem(it).total_price, 0);
  const parsedDiscountPct = discountPct === '' || discountPct === null || discountPct === undefined ? 0 : parseFloat(discountPct);
  const parsedTaxPct = taxPct === '' || taxPct === null || taxPct === undefined ? 3 : parseFloat(taxPct);
  const discount = subtotal * (Number.isFinite(parsedDiscountPct) ? parsedDiscountPct : 0) / 100;
  const tax_base = subtotal - discount;
  const tax = tax_base * (Number.isFinite(parsedTaxPct) ? parsedTaxPct : 3) / 100;
  return { subtotal, discount, tax_base, tax, total: tax_base + tax };
}

// ── Customer Autocomplete ─────────────────────────────────────────────────────
function CustomerSearch({ onSelect }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);

  const loadInitialCustomers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/sales/customers', { params: { page: 1, limit: 20 } });
      setResults(res?.data?.data || []);
    } catch {
      setResults([]);
    }
    setLoading(false);
  }, []);

  const search = useCallback((term) => {
    clearTimeout(timer.current);
    if (!term.trim()) {
      loadInitialCustomers();
      return;
    }
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await api.get('/sales/customers', { params: { search: term, page: 1, limit: 20 } });
        setResults(res?.data?.data || []);
      } catch { setResults([]); }
      setLoading(false);
    }, 300);
  }, [loadInitialCustomers]);

  useEffect(() => {
    loadInitialCustomers();
  }, [loadInitialCustomers]);

  return (
    <div className="qc-autocomplete">
      <div style={{ position: 'relative' }}>
        <Search size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
        <input
          className="qc-input"
          style={{ paddingLeft: 38 }}
          placeholder="Search or select available customer…"
          value={q}
          onChange={e => { setQ(e.target.value); search(e.target.value); }}
        />
        {loading && <Loader size={14} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', color: '#6366f1' }} />}
      </div>
      {(results.length > 0 || loading) && (
        <div className="qc-dropdown" style={{ position: 'static', marginTop: 8, border: '1.5px solid #e2e8f0', borderRadius: 10 }}>
          {!loading && results.length === 0 && (
            <div className="qc-dropdown-item" style={{ color: '#94a3b8' }}>
              No customers found
            </div>
          )}
          {results.map(c => (
            <div
              key={c.id}
              className="qc-dropdown-item"
              onClick={() => { onSelect(c); setResults([]); setQ(''); }}
            >
              <div style={{ fontWeight: 600 }}>{c.name}</div>
              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
                {(c.customer_category || c.category) && <span style={{ marginRight: 8, color: '#6366f1' }}>{c.customer_category || c.category}</span>}
                {c.city && <span style={{ marginRight: 8 }}>{c.city}</span>}
                {c.phone && <span>{c.phone}</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Design Autocomplete ───────────────────────────────────────────────────────
function DesignSearch({ value, onChange }) {
  const [q, setQ] = useState(value || '');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);

  const loadInitialDesigns = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/master-design/designs', { params: { status: 'ACTIVE', page: 1, per_page: 20 } });
      setResults(res.data.designs || res.data.data || []);
    } catch {
      setResults([]);
    }
    setLoading(false);
  }, []);

  const search = useCallback((term) => {
    clearTimeout(timer.current);
    if (!term.trim()) {
      loadInitialDesigns();
      return;
    }
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await api.get('/master-design/designs', { params: { q: term, status: 'ACTIVE', per_page: 10 } });
        setResults(res.data.designs || res.data.data || []);
      } catch { setResults([]); }
      setLoading(false);
    }, 300);
  }, [loadInitialDesigns]);

  useEffect(() => {
    if (!value) loadInitialDesigns();
  }, [value, loadInitialDesigns]);

  return (
    <div className="qc-autocomplete">
      <div style={{ position: 'relative' }}>
        <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
        <input
          className="qc-input"
          style={{ paddingLeft: 32, fontSize: 13 }}
          placeholder="Search or select available design…"
          value={q}
          onChange={e => { setQ(e.target.value); search(e.target.value); }}
        />
        {loading && <Loader size={12} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', color: '#6366f1' }} />}
      </div>
      {(results.length > 0 || loading) && (
        <div className="qc-dropdown" style={{ position: 'static', marginTop: 8, border: '1.5px solid #e2e8f0', borderRadius: 10 }}>
          {!loading && results.length === 0 && (
            <div className="qc-dropdown-item" style={{ color: '#94a3b8' }}>
              No designs found
            </div>
          )}
          {results.map(d => (
            <div
              key={d.id}
              className="qc-dropdown-item"
              onClick={() => { onChange(d); setResults([]); setQ(`${d.design_code} – ${d.design_name}`); }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                {d.image_url
                  ? <img src={d.image_url} alt="" style={{ width: 32, height: 32, borderRadius: 6, objectFit: 'cover' }} />
                  : <div style={{ width: 32, height: 32, background: '#f1f5f9', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: 10 }}>IMG</div>
                }
                <div>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{d.design_code}</div>
                  <div style={{ fontSize: 11, color: '#64748b' }}>{d.design_name} · {d.actual_weight}g</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── BOM Table ─────────────────────────────────────────────────────────────────
function BomEstimate({ design }) {
  if (!design) return null;
  const stages = Array.isArray(design.stages) ? design.stages : [];
  const hasMaterials = stages.some(s => (s.default_materials || []).length > 0);
  return (
    <div>
      <div style={{ fontSize: 12, color: '#1a1a1a', marginBottom: 6 }}>
        Metal Weight: <strong>{design.actual_weight || 0}g</strong>
      </div>
      {hasMaterials && (
        <table className="qc-bom-table">
          <thead>
            <tr><th>Stage</th><th>Material</th><th>Qty</th><th>Unit</th><th>Est. Cost</th></tr>
          </thead>
          <tbody>
            {stages.map(s => (s.default_materials || []).map((m, mi) => (
              <tr key={`${s.id}-${mi}`}>
                <td>{s.stage_name}</td>
                <td>{m.material_name || m.material_id || '—'}</td>
                <td>{m.quantity || m.qty || '—'}</td>
                <td>{m.unit || 'g'}</td>
                <td>₹{fmt(m.estimated_cost || 0)}</td>
              </tr>
            )))}
          </tbody>
        </table>
      )}
      {!hasMaterials && <div style={{ fontSize: 12, color: '#94a3b8', fontStyle: 'italic' }}>No BOM materials defined for this design.</div>}
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function QuotationCreate({ onBack, onCreated, initialDesign = null }) {
  const { getValues } = useMasterData('METAL_TYPE');
  const metalTypes = getValues('METAL_TYPE');
  const [step, setStep] = useState(1);
  const [customer, setCustomer] = useState(null);
  const [items, setItems] = useState(() => {
    if (initialDesign) {
      return [{
        ...emptyItem(),
        design_id: initialDesign.id,
        design: initialDesign,
        design_search: `${initialDesign.design_code} – ${initialDesign.design_name}`,
      }];
    }
    return [emptyItem()];
  });
  const [form, setForm] = useState({
    subject: '',
    valid_until: '',
    discount_pct: 0,
    tax_pct: 3,
    terms: '50% advance, balance on delivery.',
    internal_notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [created, setCreated] = useState(null);

  useEffect(() => {
    if (!customer?.name) return;
    const nextSubject = buildQuotationSubject(customer.name);
    setForm((prev) => {
      const current = (prev.subject || '').trim();
      const previousAuto = buildQuotationSubject('');
      if (!current || current === previousAuto || current.endsWith(' Quotation')) {
        return { ...prev, subject: nextSubject };
      }
      return prev;
    });
  }, [customer]);

  useEffect(() => {
    let mounted = true;
    const hydrateFromUrl = async () => {
      if (initialDesign) return;
      const params = new URLSearchParams(window.location.search || '');
      const designId = params.get('design_id');
      if (!designId) return;
      try {
        const res = await api.get(`/master-design/designs/${designId}`);
        const raw = res?.data?.data || {};
        const design = {
          id: raw.design_id || Number(designId),
          design_id: raw.design_id || Number(designId),
          design_code: raw.design_code,
          design_name: raw.design_name,
          image_url: raw.image || '',
          actual_weight: raw.actual_weight,
          category: raw.category_name || raw.category,
          description: raw.description || '',
          complexity_weight: raw.complexity_weight,
        };
        if (!mounted) return;
        setItems([{
          ...emptyItem(),
          design_id: design.id,
          design,
          design_search: `${design.design_code || ''} – ${design.design_name || ''}`.trim(),
        }]);
      } catch {
        // ignore silent fallback
      }
    };
    hydrateFromUrl();
    return () => { mounted = false; };
  }, [initialDesign]);

  useEffect(() => {
    if (!metalTypes.length) return;
    setItems((prev) => prev.map((item) => (item.metal_type ? item : { ...item, metal_type: metalTypes[0] })));
  }, [metalTypes]);

  // ── Item helpers ────────────────────────────────────────────────────────────
  const updateItem = (idx, patch) => {
    setItems(prev => prev.map((it, i) => i === idx ? { ...it, ...patch } : it));
  };

  const addItem = () => setItems(prev => [...prev, { ...emptyItem(), metal_type: metalTypes[0] || '22K Gold' }]);

  const removeItem = (idx) => {
    setItems(prev => prev.filter((_, i) => i !== idx));
  };

  // ── Validation ──────────────────────────────────────────────────────────────
  const validateStep = () => {
    if (step === 1) {
      if (!customer) { setError('Please select a customer.'); return false; }
      if (customer.is_blacklisted) { setError('Cannot create quotation for a blacklisted customer.'); return false; }
    }
    if (step === 2) {
      for (const it of items) {
        if (!it.design_id && !it.design) { setError('Please select a design for each row.'); return false; }
        if (!it.quantity || it.quantity < 1) { setError('Quantity must be at least 1.'); return false; }
      }
    }
    if (step === 3) {
      if (!form.subject.trim()) { setError('Please enter a subject.'); return false; }
      if (!form.valid_until) { setError('Please select a valid until date.'); return false; }
    }
    setError('');
    return true;
  };

  const next = () => { if (validateStep()) setStep(s => s + 1); };
  const back = () => { setError(''); setStep(s => s - 1); };

  // ── Submit ──────────────────────────────────────────────────────────────────
  const submit = async (submitForApproval = false) => {
    setSaving(true);
    setError('');
    try {
      const payload = {
        customer_id: customer.id,
        subject: form.subject,
        valid_until: form.valid_until,
        discount_pct: form.discount_pct === '' ? 0 : parseFloat(form.discount_pct),
        tax_pct: form.tax_pct === '' ? 3 : parseFloat(form.tax_pct),
        terms: form.terms,
        internal_notes: form.internal_notes,
        items: items.map(it => {
          const { unit_metal_price } = computeItem(it);
          return {
            design_id: it.design_id || it.design?.id || it.design?.design_id,
            quantity: parseInt(it.quantity),
            metal_type: it.metal_type,
            metal_rate: parseFloat(it.metal_rate || 0),
            unit_metal_price,
            making_charges: parseFloat(it.making_charges || 0),
            stone_charges: parseFloat(it.stone_charges || 0),
            other_charges: parseFloat(it.other_charges || 0),
            unit_price: computeItem(it).unit_price,
            special_requirements: it.special_requirements,
          };
        }),
      };

      const res = await api.post('/quotations', payload);
      const { id } = res.data;

      if (submitForApproval) {
        await api.post(`/quotations/${id}/submit-approval`);
      }

      setCreated(res.data);
      onCreated?.(res.data);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to create quotation. Please try again.');
    }
    setSaving(false);
  };

  // ── Totals ──────────────────────────────────────────────────────────────────
  const totals = computeTotals(items, form.discount_pct, form.tax_pct);

  // ── Success screen ──────────────────────────────────────────────────────────
  if (created) {
    return (
      <>
        <style>{S}</style>
        <div className="qc-wrap">
          <div className="qc-card qc-success-block">
            <CheckCircle size={56} color="#10b981" style={{ margin: '0 auto 16px' }} />
            <h2 style={{ color: '#1e293b', margin: '0 0 8px' }}>Quotation Created!</h2>
            <div style={{ color: '#1a1a1a', marginBottom: 24 }}>
              <strong>{created.quotation_no}</strong> · Total ₹{fmt(created.total_amount)}
            </div>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
              <button className="qc-btn-secondary" onClick={onBack}>
                ← Back to List
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  // ── Stepper ─────────────────────────────────────────────────────────────────
  const minDate = (() => {
    const d = new Date();
    d.setDate(d.getDate() + 7);
    return d.toISOString().split('T')[0];
  })();

  return (
    <>
      <style>{S}</style>
      <div className="qc-wrap">
        {/* Back */}
        <button className="qc-btn-secondary" style={{ marginBottom: 20 }} onClick={onBack}>
          <ChevronLeft size={16} /> Back to Quotations
        </button>

        {/* Stepper */}
        <div className="qc-stepper">
          {STEPS.map((s, idx) => (
            <React.Fragment key={s.id}>
              <div className="qc-step">
                <div className={`qc-step-circle ${step > s.id ? 'done' : step === s.id ? 'active' : 'pending'}`}>
                  {step > s.id ? <CheckCircle size={16} /> : s.id}
                </div>
                <span className={`qc-step-label ${step === s.id ? 'active' : ''}`}>{s.label}</span>
              </div>
              {idx < STEPS.length - 1 && (
                <div className={`qc-connector ${step > s.id ? 'done' : ''}`} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* ── STEP 1: Customer ── */}
        {step === 1 && (
          <div className="qc-card">
            <div className="qc-section-title">Select Customer</div>
            <CustomerSearch onSelect={setCustomer} />

            {customer && customer.is_blacklisted && (
              <div className="qc-blacklist-banner">
                <AlertTriangle size={20} style={{ flexShrink: 0, marginTop: 1 }} />
                <div>
                  <strong>Customer is Blacklisted</strong>
                  <div style={{ fontSize: 13, marginTop: 2 }}>{customer.blacklist_reason || 'No reason provided.'}</div>
                </div>
              </div>
            )}

            {customer && !customer.is_blacklisted && (
              <div className="qc-customer-card">
                <div className="qc-customer-avatar">{customer.name.charAt(0).toUpperCase()}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: 16, color: '#1e293b' }}>{customer.name}</div>
                  <div style={{ fontSize: 13, color: '#1a1a1a', marginTop: 2 }}>
                    {customer.customer_category && (
                      <span style={{ background: '#eef2ff', color: '#6366f1', borderRadius: 6, padding: '2px 8px', marginRight: 8, fontSize: 11, fontWeight: 600 }}>
                        {customer.customer_category}
                      </span>
                    )}
                    {customer.city && <span style={{ marginRight: 8 }}>{customer.city}</span>}
                    {customer.phone && <span>{customer.phone}</span>}
                  </div>
                  {customer.outstanding_balance > 0 && (
                    <div style={{ marginTop: 4, fontSize: 12, color: '#f59e0b' }}>
                      Outstanding: ₹{fmt(customer.outstanding_balance)}
                    </div>
                  )}
                  {customer.rating && (
                    <div style={{ marginTop: 4 }}>
                      {'★'.repeat(customer.rating)}{'☆'.repeat(5 - customer.rating)}
                    </div>
                  )}
                </div>
                <button
                  style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: 12 }}
                  onClick={() => setCustomer(null)}
                >
                  Change
                </button>
              </div>
            )}
          </div>
        )}

        {/* ── STEP 2: Design Items ── */}
        {step === 2 && (
          <div>
            <div className="qc-card">
              <div className="qc-section-title">Design Items</div>

              {items.map((item, idx) => {
                const { unit_metal_price, unit_making_price, unit_stone_price, unit_other_price, unit_price, total_price } = computeItem(item);
                return (
                  <div className="qc-design-row" key={item._key}>
                    {items.length > 1 && (
                      <button className="qc-remove-btn" onClick={() => removeItem(idx)}>
                        <Trash2 size={12} /> Remove
                      </button>
                    )}
                    <div style={{ fontWeight: 700, color: '#6366f1', fontSize: 13, marginBottom: 14 }}>
                      Item #{idx + 1}
                    </div>

                    {/* Design search */}
                    <div className="qc-field">
                      <label className="qc-label">Design *</label>
                      {item.design ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: '#eef2ff', borderRadius: 10 }}>
                          {item.design.image_url && (
                            <img src={item.design.image_url} alt="" style={{ width: 36, height: 36, borderRadius: 6, objectFit: 'cover' }} />
                          )}
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 600, fontSize: 13 }}>{item.design.design_code} – {item.design.design_name}</div>
                            <div style={{ fontSize: 11, color: '#6366f1' }}>{item.design.actual_weight}g</div>
                          </div>
                          <button
                            style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: 12 }}
                            onClick={() => updateItem(idx, { design: null, design_id: null })}
                          >
                            Change
                          </button>
                        </div>
                      ) : (
                        <DesignSearch
                          onChange={(d) => updateItem(idx, { design: d, design_id: d.id || d.design_id || d.pk })}
                        />
                      )}
                    </div>

                    {/* Charges grid */}
                    <div className="qc-grid-4" style={{ marginTop: 12 }}>
                      <div className="qc-field">
                        <label className="qc-label">Quantity *</label>
                        <input type="number" min="1" className="qc-input" value={item.quantity}
                          onChange={e => updateItem(idx, { quantity: e.target.value })} />
                      </div>
                      <div className="qc-field">
                        <label className="qc-label">Metal Type</label>
                        <select className="qc-input" value={item.metal_type}
                          onChange={e => updateItem(idx, { metal_type: e.target.value })}>
                          {metalTypes.map(m => <option key={m}>{m}</option>)}
                        </select>
                      </div>
                      <div className="qc-field">
                        <label className="qc-label">Metal Rate (₹/g)</label>
                        <input type="number" className="qc-input" value={item.metal_rate}
                          onChange={e => updateItem(idx, { metal_rate: e.target.value })} />
                      </div>
                      <div className="qc-field">
                        <label className="qc-label">Metal Weight (g)</label>
                        <input type="text" className="qc-input" readOnly
                          value={item.design?.actual_weight || 0} />
                      </div>
                    </div>

                    <div className="qc-grid-4">
                      <div className="qc-field">
                        <label className="qc-label">Metal Price (auto)</label>
                        <input type="text" className="qc-input" readOnly value={fmt(unit_metal_price)} />
                      </div>
                      <div className="qc-field">
                        <label className="qc-label">Making Charges (₹/g)</label>
                        <input type="number" className="qc-input" value={item.making_charges}
                          onChange={e => updateItem(idx, { making_charges: e.target.value })} />
                      </div>
                      <div className="qc-field">
                        <label className="qc-label">Stone Charges (₹/g)</label>
                        <input type="number" className="qc-input" value={item.stone_charges}
                          onChange={e => updateItem(idx, { stone_charges: e.target.value })} />
                      </div>
                      <div className="qc-field">
                        <label className="qc-label">Other Charges (₹/g)</label>
                        <input type="number" className="qc-input" value={item.other_charges}
                          onChange={e => updateItem(idx, { other_charges: e.target.value })} />
                      </div>
                    </div>

                    <div className="qc-grid-3">
                      <div className="qc-field">
                        <label className="qc-label">Making Total / Piece</label>
                        <input type="text" className="qc-input" readOnly value={fmt(unit_making_price)} />
                      </div>
                      <div className="qc-field">
                        <label className="qc-label">Stone Total / Piece</label>
                        <input type="text" className="qc-input" readOnly value={fmt(unit_stone_price)} />
                      </div>
                      <div className="qc-field">
                        <label className="qc-label">Other Total / Piece</label>
                        <input type="text" className="qc-input" readOnly value={fmt(unit_other_price)} />
                      </div>
                    </div>

                    <div className="qc-grid-3">
                      <div className="qc-field">
                        <label className="qc-label">Unit Price (auto)</label>
                        <input type="text" className="qc-input" readOnly
                          value={fmt(unit_price)} style={{ fontWeight: 700, color: '#1e293b' }} />
                      </div>
                      <div className="qc-field">
                        <label className="qc-label">Line Total (auto)</label>
                        <input type="text" className="qc-input" readOnly
                          value={fmt(total_price)} style={{ fontWeight: 700, color: '#6366f1' }} />
                      </div>
                      <div className="qc-field">
                        <label className="qc-label">Special Requirements</label>
                        <input type="text" className="qc-input" placeholder="e.g. Rhodium coating"
                          value={item.special_requirements}
                          onChange={e => updateItem(idx, { special_requirements: e.target.value })} />
                      </div>
                    </div>

                    {/* BOM Estimate expandable */}
                    {item.design && (
                      <div className="qc-bom-expand">
                        <button
                          className="qc-bom-toggle"
                          onClick={() => updateItem(idx, { bom_open: !item.bom_open })}
                        >
                          {item.bom_open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                          BOM Estimate
                        </button>
                        {item.bom_open && <BomEstimate design={item.design} />}
                      </div>
                    )}
                  </div>
                );
              })}

              <button className="qc-add-row-btn" onClick={addItem}>
                <Plus size={16} /> Add Another Design
              </button>
            </div>

            {/* Live totals preview */}
            <div className="qc-totals-box">
              <div className="qc-totals-row"><span>Subtotal</span><span>₹{fmt(totals.subtotal)}</span></div>
              <div className="qc-totals-row" style={{ color: '#c7d2fe' }}>
                <span>Discount ({form.discount_pct}%)</span><span>-₹{fmt(totals.discount)}</span>
              </div>
              <div className="qc-totals-row" style={{ color: '#c7d2fe' }}>
                <span>Tax ({form.tax_pct}%)</span><span>+₹{fmt(totals.tax)}</span>
              </div>
              <hr className="qc-totals-divider" />
              <div className="qc-totals-row qc-totals-grand"><span>TOTAL</span><span>₹{fmt(totals.total)}</span></div>
            </div>
          </div>
        )}

        {/* ── STEP 3: Terms ── */}
        {step === 3 && (
          <div className="qc-card">
            <div className="qc-section-title">Quotation Terms</div>
            <div className="qc-grid-2">
              <div className="qc-field">
                <label className="qc-label">Quotation Subject *</label>
                <input type="text" className="qc-input" placeholder="Selected customer name will appear here automatically"
                  value={form.subject} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))} />
                <div style={{ fontSize: 12, color: '#6366f1', marginTop: 4 }}>
                  Customer name comes from the selected customer.
                </div>
              </div>
              <div className="qc-field">
                <label className="qc-label">Valid Until *</label>
                <input type="date" className="qc-input" min={minDate}
                  value={form.valid_until} onChange={e => setForm(f => ({ ...f, valid_until: e.target.value }))} />
              </div>
            </div>

            <div className="qc-grid-2">
              <div className="qc-field">
                <label className="qc-label">Discount %</label>
                <input type="number" className="qc-input" min="0" max="100" step="0.5"
                  value={form.discount_pct} onChange={e => setForm(f => ({ ...f, discount_pct: e.target.value }))} />
                <div style={{ fontSize: 12, color: '#6366f1', marginTop: 4 }}>
                  Discount Amount: ₹{fmt(totals.discount)}
                </div>
              </div>
              <div className="qc-field">
                <label className="qc-label">Tax (GST) %</label>
                <input type="number" className="qc-input" min="0" max="28" step="0.5"
                  value={form.tax_pct} onChange={e => setForm(f => ({ ...f, tax_pct: e.target.value }))} />
                <div style={{ fontSize: 12, color: '#6366f1', marginTop: 4 }}>
                  Tax Amount: ₹{fmt(totals.tax)}
                </div>
              </div>
            </div>

            <div className="qc-field">
              <label className="qc-label">Terms & Conditions</label>
              <textarea className="qc-input" rows={4} style={{ resize: 'vertical' }}
                value={form.terms} onChange={e => setForm(f => ({ ...f, terms: e.target.value }))} />
            </div>

            <div className="qc-field">
              <label className="qc-label">Internal Notes (not printed on PDF)</label>
              <textarea className="qc-input" rows={2} style={{ resize: 'vertical' }}
                value={form.internal_notes} onChange={e => setForm(f => ({ ...f, internal_notes: e.target.value }))} />
            </div>
          </div>
        )}

        {/* ── STEP 4: Summary ── */}
        {step === 4 && (
          <div>
            <div className="qc-card">
              <div className="qc-section-title">Summary — {customer?.name}</div>
              <div style={{ fontSize: 13, color: '#1a1a1a', marginBottom: 16 }}>
                <strong>{form.subject}</strong> · Valid until {form.valid_until}
              </div>

              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#f8fafc' }}>
                    {['Design', 'Metal Type', 'Qty', 'Unit Price', 'Line Total'].map(h => (
                      <th key={h} style={{ padding: '10px 12px', textAlign: 'left', color: '#1a1a1a', fontWeight: 600, borderBottom: '2px solid #e2e8f0' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {items.map((it, idx) => {
                    const { unit_price, total_price } = computeItem(it);
                    return (
                      <tr key={it._key} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '10px 12px', fontWeight: 600 }}>
                          {it.design ? `${it.design.design_code} – ${it.design.design_name}` : '—'}
                        </td>
                        <td style={{ padding: '10px 12px' }}>{it.metal_type}</td>
                        <td style={{ padding: '10px 12px' }}>{it.quantity}</td>
                        <td style={{ padding: '10px 12px' }}>₹{fmt(unit_price)}</td>
                        <td style={{ padding: '10px 12px', fontWeight: 700, color: '#1e293b' }}>₹{fmt(total_price)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="qc-totals-box">
              <div className="qc-totals-row"><span>Subtotal</span><span>₹{fmt(totals.subtotal)}</span></div>
              <div className="qc-totals-row" style={{ color: '#c7d2fe' }}>
                <span>Discount ({form.discount_pct}%)</span><span>-₹{fmt(totals.discount)}</span>
              </div>
              <div className="qc-totals-row" style={{ color: '#c7d2fe' }}>
                <span>GST ({form.tax_pct}%)</span><span>+₹{fmt(totals.tax)}</span>
              </div>
              <hr className="qc-totals-divider" />
              <div className="qc-totals-row qc-totals-grand"><span>TOTAL</span><span>₹{fmt(totals.total)}</span></div>
            </div>
          </div>
        )}

        {/* Error */}
        {error && <div className="qc-error-msg" style={{ marginTop: 12 }}><AlertTriangle size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />{error}</div>}

        {/* Navigation */}
        <div className="qc-nav-row">
          <div>
            {step > 1 && (
              <button className="qc-btn-secondary" onClick={back}>
                <ChevronLeft size={16} /> Back
              </button>
            )}
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            {step < 4 && (
              <button className="qc-btn-primary" onClick={next}>
                Next <ChevronRight size={16} />
              </button>
            )}
            {step === 4 && (
              <>
                <button className="qc-btn-secondary" onClick={() => submit(false)} disabled={saving}>
                  {saving ? <Loader size={14} /> : null} Save as Draft
                </button>
                <button className="qc-btn-success" onClick={() => submit(true)} disabled={saving}>
                  {saving ? <Loader size={14} /> : <CheckCircle size={14} />}
                  Submit for Approval
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
